import { type ProductData } from '@/components/scan-qr/ConfirmationPage';
import { base64ToFile, fileToBase64 } from './fileStorage';

const CACHE_PREFIX = 'qr_flow_cache_';
const INACTIVITY_LIMIT_MS = 10 * 60 * 1000; // 10 mins

export interface QRCacheData {
    timestamp: number;
    merchantId: string;
    productName: string;
    id?: string;
    price?: number;
    orderId: string;
    productPhotoBase64?: string | null;
    productPhotoMeta?: { name: string; type: string };
    purchaseInvoiceBase64?: string | null;
    purchaseInvoiceMeta?: { name: string; type: string };
}

export const saveQrDraft = async (merchantId: string, data: Partial<ProductData>) => {
    const payload: QRCacheData = {
        merchantId,
        timestamp: Date.now(),
        productName: data.productName || '',
        id: data.id,
        price: data.price,
        orderId: data.orderId || '',
    };

    if (data.productPhoto) {
        payload.productPhotoBase64 = await fileToBase64(data.productPhoto);
        payload.productPhotoMeta = { name: data.productPhoto.name, type: data.productPhoto.type };
    }

    if (data.purchaseInvoice) {
        payload.purchaseInvoiceBase64 = await fileToBase64(data.purchaseInvoice);
        payload.purchaseInvoiceMeta = { name: data.purchaseInvoice.name, type: data.purchaseInvoice.type };
    }

    sessionStorage.setItem(`${CACHE_PREFIX}${merchantId}`, JSON.stringify(payload));
};

export const loadQrDraft = (merchantId: string): ProductData | null => {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${merchantId}`);
    if (!raw) return null;

    try {
        const payload: QRCacheData = JSON.parse(raw);
        if (Date.now() - payload.timestamp > INACTIVITY_LIMIT_MS) {
            clearQrDraft(merchantId);
            return null;
        }

        const productData: ProductData = {
            productName: payload.productName,
            id: payload.id,
            price: payload.price,
            orderId: payload.orderId,
            productPhoto: payload.productPhotoBase64 && payload.productPhotoMeta
                ? base64ToFile(payload.productPhotoBase64, payload.productPhotoMeta.name, payload.productPhotoMeta.type)
                : null,
            purchaseInvoice: payload.purchaseInvoiceBase64 && payload.purchaseInvoiceMeta
                ? base64ToFile(payload.purchaseInvoiceBase64, payload.purchaseInvoiceMeta.name, payload.purchaseInvoiceMeta.type)
                : null,
        };

        return productData;
    } catch (e) {
        console.error("Cache parsing error", e);
        return null; // fallback gracefully if cache is corrupted
    }
};

export const pingQrActivity = (merchantId: string) => {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${merchantId}`);
    if (!raw) return;
    try {
        const payload: QRCacheData = JSON.parse(raw);
        // Only ping if it is not already expired
        if (Date.now() - payload.timestamp <= INACTIVITY_LIMIT_MS) {
            payload.timestamp = Date.now();
            sessionStorage.setItem(`${CACHE_PREFIX}${merchantId}`, JSON.stringify(payload));
        } else {
            clearQrDraft(merchantId);
        }
    } catch {
        // Ignore cache parsing errors on ping
    }
};

export const clearQrDraft = (merchantId: string) => {
    sessionStorage.removeItem(`${CACHE_PREFIX}${merchantId}`);
};
